import { useRecentArticles, type Article } from '@/hooks/useRecentArticles';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, MessageSquare } from 'lucide-react';
import { RelaySelector } from '@/components/RelaySelector';

interface ArticleCardProps {
  article: Article;
  onArticleClick: (url: string) => void;
}

function ArticleCard({ article, onArticleClick }: ArticleCardProps) {
  const timeAgo = (dateString: string) => {
    const now = new Date();
    const published = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - published.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return published.toLocaleDateString();
  };

  return (
    <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 overflow-hidden">
      <CardContent className="p-0">
        <div
          className="flex gap-4 p-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          onClick={() => onArticleClick(article.url)}
        >
          {/* Thumbnail */}
          <div className="flex-shrink-0">
            {article.thumbnail ? (
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                <img
                  src={article.thumbnail}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 flex items-center justify-center">
                        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                        </svg>
                      </div>
                    `;
                  }}
                />
              </div>
            ) : (
              <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Title */}
            <div>
              <h3 className="font-semibold text-lg leading-tight text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-3">
                {article.title}
              </h3>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 mt-auto">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArticleClick(article.url);
                }}
                className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                <MessageSquare className="h-4 w-4" />
                Discuss
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(article.url, '_blank', 'noopener,noreferrer');
                }}
                className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                title="Open original link"
              >
                <Clock className="h-3 w-3 mr-1" />
                {timeAgo(article.publishedAt)}
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ArticleSkeleton() {
  return (
    <Card className="border-gray-200 dark:border-gray-700">
      <CardContent className="p-0">
        <div className="flex gap-4 p-6">
          {/* Thumbnail skeleton */}
          <div className="flex-shrink-0">
            <Skeleton className="w-24 h-24 rounded-lg" />
          </div>

          {/* Content skeleton */}
          <div className="flex-1 flex flex-col">
            {/* Title */}
            <div className="space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </div>

            {/* Description */}
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 mt-auto">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface RecentArticlesProps {
  onArticleClick: (url: string) => void;
}

export function RecentArticles({ onArticleClick }: RecentArticlesProps) {
  const { data: articles, isLoading, error } = useRecentArticles();

  if (error) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 text-center">
          Recent Links & Discussions
        </h2>
        <Card className="border-dashed border-gray-300 dark:border-gray-600">
          <CardContent className="py-8 px-6 text-center">
            <div className="max-w-sm mx-auto space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Unable to load recent links. Try switching to a different relay to discover content.
              </p>
              <RelaySelector className="w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Trending Links
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Discover interesting content and join the conversation
        </p>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {isLoading
          ? Array.from({ length: 8 }, (_, i) => <ArticleSkeleton key={i} />)
          : articles?.map((article, index) => (
              <ArticleCard
                key={`${article.source}-${index}`}
                article={article}
                onArticleClick={onArticleClick}
              />
            ))
        }
      </div>

      {articles && articles.length === 0 && (
        <div className="max-w-4xl mx-auto">
          <Card className="border-dashed border-gray-300 dark:border-gray-600">
            <CardContent className="py-12 px-8 text-center">
              <div className="max-w-sm mx-auto space-y-6">
                <p className="text-gray-600 dark:text-gray-400">
                  No links found. Try switching to a different relay to discover content.
                </p>
                <RelaySelector className="w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}